import type { QueryClient } from '@tanstack/vue-query'
import type { NavigationGuardWithThis } from 'vue-router'
import { getActiveOrgId, setActiveOrgId } from '@/api/client'
import { useSharedAssetPlayerStore } from '@/apps/assets/stores/useSharedAssetPlayerStore'
import { landWaitingGestures } from '@/apps/core/composables/useUndoableQueue'
import { useSessionStore } from '@/apps/session/stores/session'
import { isOtherEntry, safeNext } from '@/apps/session/next'

declare module 'vue-router' {
  interface RouteMeta {
    /** A signed-out screen: a signed-in visitor is sent on to where `?next=` points. */
    guestOnly?: boolean
  }
}

export interface CreateSessionGuardOptions {
  queryClient: QueryClient
}

/**
 * The global guard: the boot gate, the session, the unproven address, and the organization the
 * URL names. Gating is ergonomics and never a control — the server refuses whatever a screen
 * would call — so what this prevents is a person walking into a screen that can only fail.
 *
 * **The organization in the URL is what `X-Org-Id` carries.** It is set here, before any view
 * of that organization mounts, and checked against the caller's memberships: a URL naming an
 * organization they are not in lands on the picker rather than on a page of 403s.
 */
export function createSessionGuard(
  opts: CreateSessionGuardOptions,
): NavigationGuardWithThis<undefined> {
  return async (to) => {
    const session = useSessionStore()
    try {
      await session.boot()
    } catch {
      // The boot gate draws the failure and its retry navigates again.
      return true
    }

    if (to.meta.guestOnly === true && session.isAuthenticated) {
      const next = safeNext(to.query.next)
      // Another entry's path — the staff console — is not this router's to resolve: the browser
      // loads that entry's document instead.
      if (next !== null && isOtherEntry(next)) {
        window.location.assign(next)
        return false
      }
      return next ?? { name: 'organizations:list' }
    }
    if (to.meta.requiresAuth !== true) return true
    if (!session.isAuthenticated) {
      return { name: 'session:login', query: { next: to.fullPath } }
    }
    // `base-auth` opens a session for an unproven address and says so in `email_verified`; the
    // client is what parks it until the address is proven.
    if (session.user?.email_verified === false) {
      return { name: 'session:verify-email', query: { next: to.fullPath } }
    }

    const orgId = to.params.organizationId
    if (typeof orgId !== 'string' || orgId === '') return true
    if (getActiveOrgId() === orgId && session.access?.org?.org_id === orgId) return true

    // Every cached read belonged to the organization being left. With none active the cache still
    // holds the last one's: a refused pick drops the header, not what was read under it.
    const previous = getActiveOrgId()
    if (previous !== orgId) {
      // A phone's gesture still in its undo window was made in the organization being left: it is
      // sent, and answered, under that one's header before the header changes.
      if (previous !== null) await landWaitingGestures()
      opts.queryClient.clear()
      // The mini-player outlives the view the layout remounts, and it plays the organization
      // being left's asset.
      useSharedAssetPlayerStore().close()
    }
    setActiveOrgId(orgId)
    try {
      await session.refreshAccess()
    } catch (error) {
      // The navigation is abandoned and the screen stays the previous organization's, so its
      // next reads must go on naming it.
      setActiveOrgId(previous)
      throw error
    }
    if (session.access?.org?.org_id === orgId) return true
    setActiveOrgId(null)
    return { name: 'organizations:list', query: { pick: '1' } }
  }
}
